import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Spinner,
  Dropdown,
  Button,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import useFormattedDate from "hooks/useFormatDate";
import { Empty, Input, Select } from "antd";

import useDebounce from "hooks/route-rules/useDebounce"; 
import useUserAgreements from "hooks/useUserAgreements";
import useAgreementStatusTransitions from "hooks/useAgreementStatusTransitions";

const UserAgreements = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchRequest = debouncedSearchTerm.trim().length >= 3;

  const {
    agreements = [],
    loading,
    error,
    refetch,
    searchAgreements,
    updateAgreementStatus,
    totalPages,
    totalElements,
  } = useUserAgreements(page, pageSize);

  const { formatDateTime } = useFormattedDate();

  const {
    showStatusModal,
    setShowStatusModal,
    statusChangeData,
    confirmStatusChange,
    handleStatusChange,
    getStatusModalContent,
    getValidTransitions,
    isEditable,
  } = useAgreementStatusTransitions(updateAgreementStatus, refetch);

  const handleViewAgreement = (id) => navigate(paths.agreementDetails(id));
  const handleEditAgreement = (id) => navigate(paths.agreementEdit(id));
  const handleCreateAgreement = () => navigate(paths.agreementAdd);

  const getStatusStyle = (status) => {
    const statusStyles = {
      ACTIVE: { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" },
      DRAFT: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
      READY: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      EXPIRED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      ABANDONED: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
      DELETED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      DEFAULT: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
    };
    const style = statusStyles[status] || statusStyles.DEFAULT;
    return {
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "0.7rem",
      fontWeight: 400, // Changed to normal
      lineHeight: 1.2,
      border: `1px solid ${style.border}`,
      backgroundColor: style.bg,
      color: style.text,
      display: "inline-block",
    };
  };

  const statusFilterOptions = [
    { label: "🌍 ALL STATUS", value: "ALL" },
    { label: "✅ ACTIVE", value: "ACTIVE" },
    { label: "📝 DRAFT", value: "DRAFT" },
    { label: "🟡 READY", value: "READY" },
    { label: "🔴 EXPIRED", value: "EXPIRED" },
    { label: "⚪ ABANDONED", value: "ABANDONED" },
    { label: "🚫 DELETED", value: "DELETED" },
  ];

  const refreshCurrentView = useCallback(() => {
    const trimmed = debouncedSearchTerm.trim();
    if (trimmed.length === 0 && selectedStatus === "ALL") {
      refetch();
      return;
    }
    searchAgreements(trimmed, selectedStatus, page);
  }, [debouncedSearchTerm, page, refetch, searchAgreements, selectedStatus]);

  useEffect(() => { refreshCurrentView(); }, [refreshCurrentView]);

const columns = useMemo(() => [
  {
    accessorKey: "id",
    header: "Agreement ID",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => {
      const fullId = getValue() || "";
      return (
        <div 
          className="text-900 font-monospace fw-normal" // Changed to normal weight
          title={fullId} 
          style={{ 
            maxWidth: 120, 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            fontSize: 14, 
            cursor: 'help' 
          }}
        >
          {fullId}
        </div>
      );
    },
  },
  {
    accessorKey: "version",
    header: "Version",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => (
      <span className="text-900 fw-normal" style={{ fontSize: 12 }}>
        {getValue()}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ getValue }) => {
      const value = String(getValue() || "UNKNOWN").toUpperCase();
      const pillStyle = {
        ...getStatusStyle(value),
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: 12,
        lineHeight: 1.2,
        fontWeight: 600, // Status pill text remains semi-bold for legibility
        display: "inline-block",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        textTransform: "uppercase"
      };

      return (
        <div className="text-center">
          <span style={pillStyle}>{value.replaceAll("_", " ")}</span>
        </div>
      );
    },
    meta: {
      headerProps: { className: "text-900 text-center fw-bold" },
      cellProps: { className: "text-center" },
    },
  },
  {
    accessorKey: "createdAt",
    header: "Creation Date",
    meta: { headerProps: { className: "text-900 fw-bold" } }, // Bold Header
    cell: ({ getValue }) => (
      <span className="text-500 fw-normal" style={{ fontSize: 13 }}> {/* Changed to normal weight */}
        {formatDateTime(getValue())}
      </span>
    ),
  },
  {
    id: "actions",
    header: "Actions",
    meta: {
      headerProps: { className: "text-900 text-center fw-bold" },
      cellProps: { className: "text-center" },
    },
    cell: ({ row }) => {
      const item = row.original;
      const validTransitions = getValidTransitions(item.status);
      return (
        <Dropdown align="end">
          <Dropdown.Toggle as="button" className="bg-transparent border-0 p-0 shadow-none text-secondary fs-9">
            ⋯
          </Dropdown.Toggle>
          <Dropdown.Menu className="py-2 fw-normal">
            {isEditable(item.status) && (
              <Dropdown.Item onClick={() => handleEditAgreement(item.id)}>Edit Content</Dropdown.Item>
            )}
            <Dropdown.Item onClick={() => handleViewAgreement(item.id)}>View Details</Dropdown.Item>
            {validTransitions.length > 0 && (
              <>
                <Dropdown.Divider />
                <Dropdown.Header className="fs-11 text-uppercase fw-normal">Change Status</Dropdown.Header>
                {validTransitions.map((t, i) => (
                  <Dropdown.Item 
                    key={i} 
                    onClick={() => confirmStatusChange(item.id, t.targetStatus, item.status, item.version)}
                  >
                    {t.label}
                  </Dropdown.Item>
                ))}
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      );
    },
  },
], [formatDateTime, getValidTransitions, isEditable, confirmStatusChange, handleEditAgreement, handleViewAgreement]);

  const table = useAdvanceTable({
    data: agreements,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  const modalContent = statusChangeData.version ? getStatusModalContent(statusChangeData.newStatus, statusChangeData.currentStatus, statusChangeData.version) : {};

  return (
    <>
      <WidgetSectionTitle
        icon="handshake"
        title="User Agreements"
        subtitle="Manage all versions of Terms of Service and Privacy Policies."
        className="mb-3 mt-3 fw-normal"
      />

      <div className="d-flex justify-content-end mb-4">
        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          onClick={handleCreateAgreement}
          className="text-xs text-white px-3 fw-normal"
          style={{ height: 32 }}
        >
          <span className="ms-1">Add Agreement</span>
        </IconButton>
      </div>

      <AdvanceTableProvider {...table}>
        <Row className="mb-2 g-3 align-items-center">
          <Col md={8} lg={9}>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <Input
                prefix={<span style={{ fontSize: 14 }}>🔍</span>}
                placeholder="Search agreements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                className="fw-normal"
                style={{ borderRadius: 12, height: 30, flex: "1 1 300px", maxWidth: 300, fontSize: "13px" }}
              />
              <Select
                value={selectedStatus}
                onChange={(val) => { setSelectedStatus(val); setPage(0); }}
                options={statusFilterOptions}
                className="fw-normal"
                style={{ minWidth: 180, height: 30 }}
              />
            </div>
          </Col>
          <Col md={4} lg={3} className="text-end">
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Results: <span className="text-dark fw-normal">{agreements.length}</span> of{" "}
              <span className="text-dark fw-normal">{totalElements || 0}</span>
            </div>
          </Col>
        </Row>

        <div className="position-relative">
          {loading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
              style={{ zIndex: 10, backgroundColor: agreements.length > 0 ? 'rgba(255, 255, 255, 0.6)' : '#fff', minHeight: 300 }}>
              <div className="text-center">
                <Spinner animation="border" variant="success" size="sm" />
                <div className="mt-2 text-500 fs-10 fw-normal">Syncing agreements...</div>
              </div>
            </div>
          )}

          {agreements.length > 0 ? (
            <>
              <div className="mx-n1 px-1">
                <AdvanceTable
                  headerClassName="bg-light text-nowrap align-middle text-dark fw-normal"
                  rowClassName="align-middle white-space-nowrap fw-normal"
                  tableProps={{
                    bordered: true,
                    striped: true,
                    className: "fs-10 mb-0 overflow-hidden fw-normal",
                  }}
                />
              </div>
              <div className="mt-3">
                <TablePaginationFooter
                  page={page}
                  totalPages={totalPages || 0}
                  totalElements={totalElements || 0}
                  onPrev={page > 0 ? () => setPage(page - 1) : undefined}
                  onNext={page < totalPages - 1 ? () => setPage(page + 1) : undefined}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                  pageSizeOptions={[10, 20, 30, 50, 100]}
                />
              </div>
            </>
          ) : !loading && (
            <div className="py-5 bg-white rounded-3 border border-200 text-center fw-normal">
              <Empty description="No agreements found" />
            </div>
          )}
        </div>
      </AdvanceTableProvider>

      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
        <Modal.Header closeButton><Modal.Title className="fw-normal">{modalContent.title}</Modal.Title></Modal.Header>
        <Modal.Body className="fw-normal">{modalContent.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" className="fw-normal" onClick={() => setShowStatusModal(false)}>Cancel</Button>
          <Button variant={modalContent.confirmVariant} size="sm" className="fw-normal" onClick={() => handleStatusChange(statusChangeData.id, statusChangeData.newStatus)}>
            {modalContent.confirmLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserAgreements;